import React, { useEffect, useState } from "react";
import "./index.css";
import { Button, Spin } from "antd";
import { useDispatch } from "react-redux";
import { changeHeader } from "../../../redux/reducer/headerSlice";
import handleGet from "../../../utils/Get";
import handlePost from "../../../utils/Post";
import { BASE_URL } from "../../../utils/config";
import { useNavigate } from "react-router";
import { MdManageAccounts } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const MyAssets = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const [width, setWidth] = useState(window.innerWidth);
  const [netWorth, setNetWorth] = useState({ value: "", loading: true });
  const [userNote, setNote] = useState({ value: "", loading: true });
  const [data, setData] = useState("");

  // ── Swap state ──
  const [assets, setAssets] = useState([]);
  const [fromAsset, setFromAsset] = useState("");
  const [toAsset, setToAsset] = useState("");
  const [amount, setAmount] = useState(null);
  const [exchangeData, setExchangeData] = useState("");
  const [swapping, setSwapping] = useState(false);
  const [loader, setLoader] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { MANAGEASSETS, MyAssets, CURRENCY, BALANCE, ACTION, Manage } = t(
    "AssetsPage",
    { returnObjects: true }
  );
  const { Swap, my, net, worth } = t("dashboard", { returnObjects: true });

  // ── Fetch net worth ──
  const fetchNetWorth = async () => {
    try {
      const res = await handleGet(`${BASE_URL}get/user/networth`);
      setNetWorth({ value: res?.networth, loading: false });
      setNote({ value: res?.userNote, loading: false });
    } catch (error) {
      console.error(error);
    }
  };

  // ── Fetch assets ──
  const fetchAssets = async () => {
    try {
      const res = await handleGet(`${BASE_URL}active/user/assets`);
      setData(res?.assets);
      setAssets(res?.assets || []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    dispatch(changeHeader(`${user?.name}, ${MyAssets}`));
  }, [user, MyAssets]);

  useEffect(() => {
    fetchNetWorth();
    fetchAssets();
  }, []);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNavigate = (value) =>
    navigate("/user/wallet/manage", { state: value });

  const handleExchange = async (amt, from, to) => {
    if (!amt || !from || !to) return;
    setLoader(true);
    try {
      const response = await handlePost(
        `${BASE_URL}currencyexchange/onchange`,
        { amount: amt, from_asset_id: from, to_asset_id: to }
      );
      if (response) setExchangeData(response?.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  const submitSwap = async () => {
    setSwapping(true);
    try {
      await handlePost(`${BASE_URL}currency/exchange`, {
        amount,
        from_asset_id: fromAsset,
        to_asset_id: toAsset,
      });
      fetchNetWorth();
      fetchAssets();
      setFromAsset("");
      setToAsset("");
      setAmount(null);
      setExchangeData("");
    } catch (error) {
      console.error(error);
    } finally {
      setSwapping(false);
    }
  };

  return (
    <section className="px-2 sm:px-0">
      <div className="grid gap-5 lg:grid-cols-2 mb-8">

        {/* ── Net Worth Card ── */}
        <div className="w-full relative rounded-[30px] bg-gradient-to-br from-[#7b5cff] to-[#5b3df5] p-3 text-white shadow-[0_20px_40px_rgba(0,0,0,0.4)]">
          <div className="flex justify-between items-start">
            <div className="px-2 py-2">
              <p className="text-xs tracking-[0.25em] uppercase text-indigo-100">
                {my} {net} {worth}
              </p>
              {netWorth.loading ? (
                <div className="h-14 w-full max-w-xs bg-white/10 animate-pulse rounded-xl mt-2" />
              ) : (
                <h2 className="text-[32px] sm:text-[48px] font-semibold mt-2 leading-none">
                  ${Number(netWorth.value).toLocaleString()}
                </h2>
              )}
              {!userNote.loading && userNote.value && (
                <p className="text-emerald-300 text-sm mt-1">{userNote.value}</p>
              )}
            </div>
          </div>

          <div className="h-px bg-white/20 my-4" />

          {/* Quick actions */}
          <div className="grid grid-cols-3 gap-2 items-center">
            <Link onClick={() => {
              window.scrollBy({
                top: 500,
                behavior: "smooth",
              });
            }} className="flex flex-col items-center gap-2">
              <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0e1629] text-xl shadow-[0_18px_40px_rgba(15,23,42,0.75)]">
                →
              </span>
              <span className="text-sm font-semibold text-indigo-50">Send</span>
            </Link>
            <Link to={'/user/swapcrypto'} className="flex flex-col items-center gap-2">
              <span className="inline-flex h-16 w-16 -translate-y-1 items-center justify-center rounded-2xl bg-white text-xl text-[#6d4cf7] shadow-[0_18px_40px_rgba(15,23,42,0.75)]">
                ⇄
              </span>
              <span className="text-sm font-semibold text-indigo-50">{Swap}</span>
            </Link>
            <Link onClick={() => {
              window.scrollBy({
                top: 500,
                behavior: "smooth",
              });
            }} className="flex flex-col items-center gap-2">
              <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0e1629] text-xl shadow-[0_18px_40px_rgba(15,23,42,0.75)]">
                ↓
              </span>
              <span className="text-sm font-semibold text-indigo-50">Withdraw</span>
            </Link>
          </div>
        </div>

        {/* ── Swap Card ── */}
        <div className="w-full rounded-3xl bg-[#1e293b] px-4 py-4 shadow-2xl">
          <h2 className="text-white text-lg font-semibold mb-3 flex items-center gap-2">
            ⇅ Swap
          </h2>

          {/* SEND */}
          <div className="bg-[#efe4c9] rounded-2xl p-4 mb-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-stone-500 mb-2">
              Send
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <input
                type="number"
                placeholder="0.00"
                value={amount ?? ""}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : null;
                  setAmount(val);
                  handleExchange(val, fromAsset, toAsset);
                }}
                className="flex-1 w-full max-w-full bg-transparent border-none outline-none text-2xl sm:text-3xl font-semibold text-stone-800 placeholder-stone-400"
                style={{
                  appearance: "textfield",
                  MozAppearance: "textfield",
                  WebkitAppearance: "none",
                }}
              />
              <div className="relative flex-shrink-0 w-full sm:w-auto">
                <select
                  value={fromAsset}
                  onChange={(e) => {
                    setFromAsset(e.target.value);
                    handleExchange(amount, e.target.value, toAsset);
                  }}
                  className="appearance-none w-full sm:w-auto bg-[#1e293b] text-slate-100 font-semibold text-sm rounded-xl pl-3 pr-8 py-2 border-none outline-none cursor-pointer"
                >
                  <option value="">Select</option>
                  {assets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                  ▾
                </span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="flex justify-center -my-2 relative">
            <button className="h-11 w-11 rounded-full bg-[#0f172a] border-2 border-slate-700 flex items-center justify-center text-white text-lg shadow-lg hover:rotate-180 transition-transform duration-300">
              ⇅
            </button>
          </div>

          {/* RECEIVE */}
          <div className="bg-[#cfe3df] rounded-2xl p-4 mt-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-2">
              Receive
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <input
                type="number"
                placeholder="0.00"
                value={exchangeData || ""}
                readOnly
                className="flex-1 w-full max-w-full bg-transparent border-none outline-none text-2xl sm:text-3xl font-semibold text-teal-900 placeholder-teal-400 cursor-default"
                style={{
                  appearance: "textfield",
                  MozAppearance: "textfield",
                  WebkitAppearance: "none",
                }}
              />
              <div className="relative flex-shrink-0 w-full sm:w-auto">
                <select
                  value={toAsset}
                  onChange={(e) => {
                    setToAsset(e.target.value);
                    handleExchange(amount, fromAsset, e.target.value);
                  }}
                  className="appearance-none w-full sm:w-auto bg-[#1e293b] text-slate-100 font-semibold text-sm rounded-xl pl-3 pr-8 py-2 border-none outline-none cursor-pointer"
                >
                  <option value="">Select</option>
                  {assets
                    .filter((a) => a.id !== fromAsset)
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                </select>
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                  ▾
                </span>
              </div>
            </div>
          </div>

          {/* Swap Button */}
          <button
            onClick={submitSwap}
            disabled={swapping || !fromAsset || !toAsset || !amount}
            className="mt-4 w-full py-3.5 rounded-2xl text-white font-bold text-base
          bg-gradient-to-r from-[#734aff] to-[#5b34ea] hover:opacity-90 transition
          disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_30px_rgba(110,72,255,0.4)]"
          >
            {swapping ? "Swapping..." : "Swap Now"}
          </button>
        </div>
      </div>

      {/* ── Assets Table ── */}
      {data ? (
        // FIX: Always allow horizontal scroll on the table wrapper
        <div className="overflow-x-auto rounded-lg bg-theme-white dark:bg-dark dark:border dark:border-[#334155] pb-2 w-full">
          <div className="min-w-[480px]">
            <div className="w-full p-4">
              <h1 className="font-nunito dark:text-white text-base md:text-2xl mb-8">
                {MANAGEASSETS}
              </h1>
              {/* FIX: Table header — use fixed layout with better column sizing */}
              <div className="grid grid-cols-[2fr_1fr_auto] gap-2 my-2 px-2">
                <span>
                  <button className="w-fit border-2 border-[#4ade80] dark:border-[#7c3aed] dark:text-gray-400 px-6 py-2 rounded-xl text-sm">
                    {CURRENCY}
                  </button>
                </span>
                <span>
                  <button className="w-fit border-2 border-[#4ade80] dark:border-[#7c3aed] dark:text-gray-400 px-6 py-2 rounded-xl text-sm">
                    {BALANCE}
                  </button>
                </span>
                <span>
                  <button className="w-fit border-2 border-[#4ade80] dark:border-[#7c3aed] dark:text-gray-400 px-6 py-2 rounded-xl text-sm">
                    {ACTION}
                  </button>
                </span>
              </div>
            </div>

            <div className="w-full">
              {Array.isArray(data) &&
                data?.map((item, index) => (
                  <div key={item?.id}>
                    {/* FIX: Same grid layout as header — [2fr_1fr_auto] */}
                    <div className="min-h-[48px] items-center grid grid-cols-[2fr_1fr_auto] gap-2 my-2 px-4">
                      {/* Currency info */}
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 flex-shrink-0 bg-dark text-white border-[0.5px] border-opacity-60 border-gray-400 rounded-full flex justify-center items-center">
                          <img
                            className="w-7 h-7 filter-class"
                            src={item?.symbol}
                            alt={item?.symbol}
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="leading-3 mb-1 text-sm sm:text-base text-[#6C5DD3] dark:text-white font-semibold font-nunito truncate">
                            {item?.name}
                          </p>
                          <p className="text-xs text-[#6C5DD3] dark:text-gray-400 font-nunito truncate">
                            {item?.short_name} ({item?.amount})
                          </p>
                        </div>
                      </div>

                      {/* Balance */}
                      <div>
                        <p className="text-xs text-[#6C5DD3] dark:text-white font-nunito">
                          {item?.amount_usd === null
                            ? "0 (USD)"
                            : `${item.amount_usd} (USD)`}
                        </p>
                      </div>

                      {/* Action */}
                      <div className="flex justify-end">
                        <Button
                          onClick={() => handleNavigate(item)}
                          className="flex items-center gap-1 py-2 bg-dark-text font-nunito text-xs sm:text-sm"
                        >
                          <MdManageAccounts size={20} />
                          <span className="hidden sm:inline">{Manage}</span>
                        </Button>
                      </div>
                    </div>

                    <div
                      className={`${index === data?.length - 1 ? "hidden" : "block"
                        } bg-hoverUser dark:border-[#334155] w-full h-[1px] my-2`}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full h-[60vh] flex justify-center items-center">
          <Spin size="large" />
        </div>
      )}
    </section>
  );
};

export default MyAssets;